
import React from 'react';
import { COLORS } from '../constants';

interface FooterProps {
  onOpenInfo: (type: 'about' | 'contact' | 'terms' | 'privacy') => void;
}

const Footer: React.FC<FooterProps> = ({ onOpenInfo }) => {
  return (
    <footer className="bg-[#1E1E1E] text-white pt-16 md:pt-24 pb-8 md:pb-12 overflow-hidden relative">
      <div className="max-w-[1440px] mx-auto px-6 md:px-20 grid lg:grid-cols-2 gap-12 lg:gap-20 mb-16 md:mb-24">
        <div className="space-y-8 md:space-y-12">
          <h2 className="fluid-heading-lg font-bold leading-tight">
            It's time to support zero pollution,<br />
            <span className="text-gray-400">with renewable resources</span>
          </h2>
          <div className="flex flex-wrap gap-4 md:gap-8">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
              </div>
              <span className="text-xs md:text-sm font-medium">Experienced for more than 10 years</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
              </div>
              <span className="text-xs md:text-sm font-medium">Support for the latest technology</span>
            </div>
          </div>
        </div>

        <div className="lg:flex lg:flex-col lg:justify-between space-y-8 lg:space-y-0">
          <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-md">
            By increasing the effectiveness and efficiency of electricity use, the use of renewable resources is very profitable for all industrial services.
          </p>
          <button 
            onClick={() => onOpenInfo('contact')}
            className="bg-white text-black font-bold px-8 md:px-10 py-3.5 md:py-4 rounded-full flex items-center gap-4 group w-fit transition-transform hover:scale-105 active:scale-95 shadow-xl shadow-white/5"
          >
            Get in touch
            <svg className="w-5 h-5 md:w-6 md:h-6 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M7 17l10-10M7 7h10v10" />
            </svg>
          </button>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-20 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-10 pt-12 md:pt-16 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className="relative -top-[1px]">
            <svg 
              className="w-7 h-7 md:w-8 md:h-8" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: COLORS.novusGreen }}
            >
              <path d="M12 20L10.5 11H13.5L12 20Z" fill="currentColor" opacity="0.2" />
              <path d="M12 20V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <g style={{ animation: 'spin 5s linear infinite', transformOrigin: '12px 11px' }}>
                <circle cx="12" cy="11" r="1.5" fill="currentColor" />
                <path d="M12 11C12 11 12.5 9 14.5 5.5C16 3 14.5 2 13 2.2C11.5 2.5 12 11 12 11Z" fill="currentColor" />
                <path d="M12 11C12 11 12.5 9 14.5 5.5C16 3 14.5 2 13 2.2C11.5 2.5 12 11 12 11Z" fill="currentColor" transform="rotate(120 12 11)" />
                <path d="M12 11C12 11 12.5 9 14.5 5.5C16 3 14.5 2 13 2.2C11.5 2.5 12 11 12 11Z" fill="currentColor" transform="rotate(240 12 11)" />
              </g>
              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </svg>
          </div>
          <span className="text-lg md:text-xl font-bold tracking-tight">Novus</span>
        </div>

        <div className="flex flex-wrap justify-center gap-6 md:gap-8 text-xs md:text-sm text-gray-400">
          <a href="#" className="hover:text-white transition-colors">Home</a>
          <button onClick={() => onOpenInfo('about')} className="hover:text-white transition-colors">About Us</button>
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#services" className="hover:text-white transition-colors">Services</a>
          <button onClick={() => onOpenInfo('contact')} className="hover:text-white transition-colors">Contact</button>
        </div>

        <div className="flex gap-4">
          {[
            { id: 'f', src: 'https://i.imgur.com/imtrVYp.png' },
            { id: 'ig', src: 'https://i.imgur.com/G65Lrmm.png' },
            { id: 'x', src: 'https://i.imgur.com/dY5SnFp.png' },
            { id: 'in', src: 'https://i.imgur.com/cGIGz0P.png' }
          ].map((social) => (
            <div key={social.id} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer border border-white/10 overflow-hidden">
              <img src={social.src} alt={social.id} className="w-5 h-5 md:w-6 md:h-6 object-contain" />
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-20 flex flex-col md:flex-row justify-between pt-10 md:pt-12 text-[9px] md:text-[10px] uppercase tracking-widest text-gray-500 font-bold text-center md:text-left">
        <div className="flex flex-col gap-1 mb-4 md:mb-0">
          <p>© 2026 Novus Energy Inc. All rights reserved</p>
          <a 
            href="https://saadmughal.space" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mt-1 px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-500/80 normal-case font-bold text-[9px] w-fit hover:bg-emerald-500/20 transition-all border border-emerald-500/10 active:scale-95 mx-auto md:mx-0"
          >
            Created by Saad with love
          </a>
        </div>
        <div className="flex justify-center gap-6 md:gap-8">
          <button onClick={() => onOpenInfo('terms')} className="hover:text-white transition-colors uppercase">Terms</button>
          <button onClick={() => onOpenInfo('privacy')} className="hover:text-white transition-colors uppercase">Privacy</button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
